import React from "react";
//#region types
import { EditableTableFieldProps, FormFieldDefinitionType, FormHandles } from "models/propTypes/genericFormProps";
import { CashReconciliationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormEnums } from "models/enums";
import { FileConstants, FormFieldConstants } from "utils/constants";
import { CashReconciliationFormProps } from "models/propTypes/cashReconciliationPropTypes";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { RevenueReportingAmountRow, RevenueReportingDifference, MaxAllowedDifference } from "components/revenueReporting";
import { TagsSection } from "components/common/dimensions/tagsSection";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const CashReconciliationCreateForm: React.ForwardRefRenderFunction<FormHandles, CashReconciliationFormProps> = (
  props,
  ref
) => {
  const { itemChangeHandler } = props;

  const amountRowFields = {
    description: {
      label: "",
      type: FormEnums.FormFieldType.TEXT,
      readonly: true,
      widthColumnCount: 6,
    },
    amount: {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormFieldType.CURRENCY,
      placeholder: TranslationKeys.Common.Amount,
      widthColumnCount: 6,
    },
  } as FormFieldDefinitionType<CashReconciliationFormTypes.CashReconciliationItem>;

  const tableExtraProps = {
    hideActions: true,
    isCompactTable: true,
    idFieldKey: FormFieldConstants.CashReconciliation.CashReconciliationItem.TemplateItemId,
  } as EditableTableFieldProps<CashReconciliationFormTypes.CashReconciliationItem>;

  const getTagRowId = (item: CashReconciliationFormTypes.CashReconciliationItem) => `${item.templateItemId}`;

  return (
    <GenericForm<CashReconciliationFormTypes.Create>
      formRef={ref}
      {...props}
      fields={[
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: TranslationKeys.Common.Attach,
          descriptionText: TranslationKeys.CashReconciliation.AttachmentsHelpText,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          innerFields: {
            filesToUpload: {
              label: " ",
              type: FormEnums.FormFieldType.FILEUPLOADER,
              extraProps: {
                fileUploaderType: "full",
                filePreviewElementId: FileConstants.DialogFilePreviewPortalId,
              },
            },
            date: {
              label: TranslationKeys.CashReconciliation.DailyCashDate,
              type: FormEnums.FormFieldType.DATE,
              extraProps: {
                maxDate: new Date(),
              },
            },
          },
        },
        {
          label: TranslationKeys.Common.Sale,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: CashReconciliationFormTypes.Create) => !values.salesItems?.length,
          innerFields: {
            salesItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            salesTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          label: TranslationKeys.Common.PaymentMethod,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: CashReconciliationFormTypes.Create) => !values.paymentItems?.length,
          innerFields: {
            paymentItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            paymentsTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          label: TranslationKeys.CashReconciliation.Other,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: CashReconciliationFormTypes.Create) => !values.otherItems?.length,
          innerFields: {
            otherItems: {
              label: "",
              type: FormEnums.FormFieldType.EDITABLETABLE,
              onChangeHandler: itemChangeHandler,
              innerFields: amountRowFields,
              extraProps: tableExtraProps,
            },
            othersTotalAmount: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingAmountRow labelKey={TranslationKeys.Common.Total} />,
            },
          },
        },
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: CashReconciliationFormTypes.Create) =>
            !values.salesItems?.length || Math.abs(values.total ?? 0) <= MaxAllowedDifference,
          innerFields: {
            total: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <RevenueReportingDifference />,
            },
          },
        },
        {
          label: TranslationKeys.Common.Tags,
          descriptionText: TranslationKeys.CashReconciliation.TagsHelpText,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          className: clsx(cssExports.revenueReportingFormSection, cssExports.noBorder),
          hidden: (values: CashReconciliationFormTypes.Create) => !values.salesItems?.length,
          innerFields: {
            dimensions: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: (
                <TagsSection<CashReconciliationFormTypes.CashReconciliationItem, CashReconciliationFormTypes.Create>
                  getRowId={getTagRowId}
                  otherHeadingKey={TranslationKeys.CashReconciliation.Other}
                  translateDimensionLabels
                />
              ),
            },
          },
        },
      ]}
      hideSuccess
      formGridLayout={{ message: false }}
    />
  );
};

export default React.forwardRef(CashReconciliationCreateForm);
